import { GreyGrey50, GreyWhite, Box } from '@sequencehq/core-components'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ScheduleTimelineGrid } from './ScheduleTimelineGrid'
import { format, isBefore } from '@sequencehq/utils/dates'
import { Provider, useAtom } from 'jotai'
import {
  scheduleDataAtom,
  TimelineScheduleData
} from './atoms/scheduleData.atom'
import { dequal } from 'dequal'
import { timelineDayOffsetsAtom } from './atoms/timeline.atom'
import {
  expandedAtom,
  timelineContainerRefAtom,
  timelineLeftScrollPositionAtom
} from './atoms/timelineUi.atom'
import {
  TIMELINE_AXIS_HEIGHT_PX,
  TIMELINE_AXIS_Z_INDEX,
  TIMELINE_DATE_OFFSET_FORMAT,
  TIMELINE_SCROLLBAR_HEIGHT_PX,
  TIMELINE_WRAPPER_HEIGHT_PX
} from './timeline.constants'
import { ScheduleTimelineLegend } from './ScheduleTimelineLegend'

const useScheduleTimelineUI = (props: {
  scheduleData: TimelineScheduleData
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInitialScrollComplete, setIsInitialScrollComplete] = useState(false)
  const [timelineDayOffsets] = useAtom(timelineDayOffsetsAtom)
  const [scheduleData, setScheduleData] = useAtom(scheduleDataAtom)
  const [, setTimelineLeftScroll] = useAtom(timelineLeftScrollPositionAtom)
  const [, setContainerRef] = useAtom(timelineContainerRefAtom)

  useLayoutEffect(() => {
    setContainerRef(containerRef.current)
  }, [setContainerRef])

  /**
   * Initialise the timeline with schedule data passed into the component.
   * This, for all intents and purposes, is our domain initialisation in
   * hexagon speak. This initialization needs to be responsive to new
   * data being passed in, so we can't use `useHydrateAtoms` in this instance.
   */
  useEffect(() => {
    if (props.scheduleData && !dequal(props.scheduleData, scheduleData)) {
      setScheduleData(props.scheduleData)
    }
  }, [scheduleData, props.scheduleData, setScheduleData])

  const todayOffset =
    timelineDayOffsets[format(new Date(), TIMELINE_DATE_OFFSET_FORMAT)]

  /**
   * On mount, and calculating today's offset, set the default
   * scroll to place today in the centre of the timeline, or the
   * end of the schedule, whichever is earlier.
   */
  useLayoutEffect(() => {
    if (containerRef.current && !isInitialScrollComplete && scheduleData) {
      const scrollOffset =
        !scheduleData?.endDate || isBefore(new Date(), scheduleData?.endDate)
          ? todayOffset
          : timelineDayOffsets[
              format(scheduleData.endDate, TIMELINE_DATE_OFFSET_FORMAT)
            ]

      containerRef.current.scrollTo({
        left: scrollOffset - containerRef.current.clientWidth * 0.5
      })
      setTimelineLeftScroll(
        scrollOffset - containerRef.current.clientWidth * 0.5
      )
      setIsInitialScrollComplete(true)
    }
  }, [
    todayOffset,
    setTimelineLeftScroll,
    scheduleData?.endDate,
    timelineDayOffsets,
    isInitialScrollComplete,
    scheduleData
  ])

  return {
    containerRef,
    setTimelineLeftScroll
  }
}

const ExpansionIndicator = () => {
  const [expanded, setExpanded] = useAtom(expandedAtom)

  return (
    <Box
      paddingTop="8px"
      opacity={0}
      transition={'opacity ease-in 0.125s'}
      cursor={expanded ? 's-resize' : 'n-resize'}
      onClick={() => {
        setExpanded(prevExpanded => !prevExpanded)
      }}
      height="16px"
      _hover={{
        opacity: 1
      }}
    >
      <Box width="100%" borderTop={`2px solid ${GreyGrey50}`}></Box>
    </Box>
  )
}
export const ScheduleTimelineUI = (props: {
  scheduleData: TimelineScheduleData
}) => {
  const { containerRef, setTimelineLeftScroll } = useScheduleTimelineUI(props)
  const [expanded] = useAtom(expandedAtom)
  const dragToScrollStateRef = useRef<{
    dragging: boolean
    currentScroll: {
      left: number
      top: number
      x: number
      y: number
    } | null
  }>({
    dragging: false,
    currentScroll: null
  })

  return (
    <Box position="relative" overflow="visible">
      <Box
        position="absolute"
        width="100%"
        top="-8px"
        zIndex={TIMELINE_AXIS_Z_INDEX + 1}
      >
        <ExpansionIndicator />
      </Box>
      <Box
        height={
          expanded
            ? `${TIMELINE_WRAPPER_HEIGHT_PX + TIMELINE_SCROLLBAR_HEIGHT_PX}px`
            : `${TIMELINE_AXIS_HEIGHT_PX}px`
        }
        transition={'height ease-in 0.25s'}
      ></Box>
      <Box
        position="absolute"
        bottom="0"
        width="100%"
        transform={`translateY(${expanded ? `0` : `calc(${TIMELINE_WRAPPER_HEIGHT_PX}px - ${TIMELINE_AXIS_HEIGHT_PX - TIMELINE_SCROLLBAR_HEIGHT_PX}px)`})`}
        transition={'transform ease-in 0.25s'}
        overflow="hidden"
      >
        <Box
          userSelect="none"
          backgroundColor={GreyWhite}
          width="100%"
          height={`${TIMELINE_WRAPPER_HEIGHT_PX + TIMELINE_SCROLLBAR_HEIGHT_PX}px`}
          ref={containerRef}
          overflow={expanded ? 'auto' : 'hidden'}
          onScroll={e => {
            const target = e.target as HTMLDivElement
            setTimelineLeftScroll(target.scrollLeft)
          }}
          position="relative"
          cursor="grab"
          onMouseDown={e => {
            dragToScrollStateRef.current = {
              dragging: true,
              currentScroll: {
                left: containerRef.current?.scrollLeft ?? 0,
                top: containerRef.current?.scrollTop ?? 0,
                x: e.clientX,
                y: e.clientY
              }
            }
          }}
          onMouseMove={e => {
            if (!dragToScrollStateRef.current.currentScroll) {
              return
            }

            if (dragToScrollStateRef.current.dragging) {
              const dx =
                e.clientX - dragToScrollStateRef.current.currentScroll.x
              const dy =
                e.clientY - dragToScrollStateRef.current.currentScroll.y

              containerRef.current?.scrollTo({
                left: dragToScrollStateRef.current.currentScroll.left - dx,
                top: dragToScrollStateRef.current.currentScroll.top - dy
              })
            }
          }}
          onMouseUp={() => {
            dragToScrollStateRef.current = {
              dragging: false,
              currentScroll: null
            }
          }}
          onMouseLeave={() => {
            dragToScrollStateRef.current = {
              dragging: false,
              currentScroll: null
            }
          }}
          zIndex={1}
        >
          <ScheduleTimelineGrid />
        </Box>
        <Box
          position="absolute"
          right="8px"
          bottom="8px"
          zIndex={2}
          opacity={0.5}
          _hover={{
            opacity: 1,
            transition: 'opacity 0.15s ease-in-out'
          }}
        >
          <ScheduleTimelineLegend />
        </Box>
      </Box>
    </Box>
  )
}

export const ScheduleTimeline = (props: {
  scheduleData: TimelineScheduleData
}) => {
  /**
   * The Jotai provider ensures we do not leak state between
   * schedules between mounts.
   */
  return (
    <Provider>
      <ScheduleTimelineUI scheduleData={props.scheduleData} />
    </Provider>
  )
}
